import { Box } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import * as Time from "@outschool/time";
import { Query } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { LazyBox, TrackedButton } from "@outschool/ui-components-shared";
import {
  ChooseATimeOrClassScheduleRequestButtonProps,
  ScheduleSuggestionsQuery
} from "@outschool/ui-components-website";
import React from "react";
import { useLocation } from "react-router-dom";

import * as Activity from "../../../../shared/Activity";
import ClassScheduleRequest from "../../ClassScheduleRequest";
import ChooseATimeModal, {
  getStartAfterDateOptions
} from "../ChooseATimeModal";

export default function ChooseATimeOrClassScheduleRequestButton({
  activity,
  leader,
  variant = "outlined",
  buttonText,
  sx
}: ChooseATimeOrClassScheduleRequestButtonProps) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\ChooseATimeOrClassScheduleRequestButton"
  );
  const location = useLocation();
  const { currentUser, isLoggedIn, userTimeZone, isEsaSession } = useSession();

  const classScheduleRequestButton = (
    <ClassScheduleRequest
      renderButton={open => (
        <TrackedButton
          variant={variant}
          onClick={open}
          trackingName={"Request a time clicked"}
          trackingUniqueId={activity.uid}
          trackViews={true}
          sx={sx}
          color="primary"
        >
          {buttonText || t`Request Another Time`}
        </TrackedButton>
      )}
      isLoggedIn={isLoggedIn}
      user={currentUser}
      // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      userTimeZone={userTimeZone}
      activity={activity}
    />
  );

  const [startAfterMonthOption] = React.useMemo(
    () =>
      getStartAfterDateOptions(
        activity.autoSchedulingDaysNoticeMin,
        activity.autoSchedulingDaysNoticeMax,
        userTimeZone
      ),
    [
      activity.autoSchedulingDaysNoticeMin,
      activity.autoSchedulingDaysNoticeMax,
      userTimeZone
    ]
  );

  if (
    (location.query && location.query.openCSR) ||
    Activity.isFlexSchedule(activity) ||
    !leader.hasAvailabilitySet ||
    !activity.isAutoSchedulingEnabled ||
    isEsaSession
  ) {
    return classScheduleRequestButton;
  }

  return (
    <LazyBox placeholder={<Box />} rootMargin="150px 0px">
      <Query
        query={ScheduleSuggestionsQuery}
        variables={{
          uid: activity.uid,
          startAfter: startAfterMonthOption.startAfter,
          startAfterTime: Time.AUTO_SCHEDULE_DAY_START_HOUR,
          endByTime: Time.AUTO_SCHEDULE_DAY_END_HOUR,
          timeZone: userTimeZone
        }}
      >
        {({
          data,
          loading,
          error
        }: {
          data: any;
          loading: boolean;
          error?: any;
        }) => {
          if (loading) {
            return null;
          }
          if (!error) {
            const {
              activity: { scheduleSuggestions }
            } = data;
            if (scheduleSuggestions.length > 0) {
              return (
                <ChooseATimeModal
                  activity={activity}
                  renderButton={(open: $TSFixMe) => (
                    <TrackedButton
                      variant={variant}
                      onClick={open}
                      trackingName="show_alternative_times"
                      trackingUniqueId={activity?.uid}
                      sx={sx}
                    >
                      {buttonText || t`Show Alternative Times`}
                    </TrackedButton>
                  )}
                />
              );
            }
          }
          return classScheduleRequestButton;
        }}
      </Query>
    </LazyBox>
  );
}
